import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import api from '@/services/api';

export type Agent = {
    id: number | null;
    first_name: string;
    last_name: string;
    email?: string;
    avatar_url?: string;

    // گیت‌ها
    is_approved?: boolean;
    request_status?: 'pending' | 'approved' | 'rejected' | string;

    // اگر قرارداد داری:
    contract_accepted?: boolean;
};

export type AuthContextType = {
    isAuthenticated: boolean;
    agent: Agent | null;
    token: string | null;
    loading: boolean;

    login: (newToken: string, agentData: Agent) => void;
    logout: () => void;

    refreshAgent: () => Promise<Agent | null>;
    setAgent: React.Dispatch<React.SetStateAction<Agent | null>>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
    return ctx;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [agent, setAgent] = useState<Agent | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);

    const logout = () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('agent_data');
        setToken(null);
        setAgent(null);
        setIsAuthenticated(false);
    };

    const login = (newToken: string, agentData: Agent) => {
        localStorage.setItem('auth_token', newToken);
        localStorage.setItem('agent_data', JSON.stringify(agentData));
        setToken(newToken);
        setAgent(agentData);
        setIsAuthenticated(true);
    };

    // ✅ از سرور وضعیت واقعی agent را می‌گیرد
    const refreshAgent = async () => {
        const storedToken = localStorage.getItem('auth_token');
        if (!storedToken) {
            logout();
            return null;
        }

        try {
            // 🔴 این مسیر را با بک‌اندت هماهنگ کن:
            // اگر مسیرت /me نیست، همین یک خط را عوض کن.
            const { data } = await api.get('/me');

            // اگر بک‌اند { agent: {...} } می‌دهد:
            const a = data?.agent ?? data;

            setAgent(a);
            localStorage.setItem('agent_data', JSON.stringify(a));
            setIsAuthenticated(true);
            return a;
        } catch (e) {
            // اگر 401 بخوری، interceptor هم می‌تونه بیرونت کنه
            return null;
        }
    };

    useEffect(() => {
        (async () => {
            const storedToken = localStorage.getItem('auth_token');
            const storedAgent = localStorage.getItem('agent_data');

            if (storedToken) {
                setToken(storedToken);
                setIsAuthenticated(true);

                if (storedAgent) setAgent(JSON.parse(storedAgent));

                // ✅ sync
                await refreshAgent();
            }

            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ اگر interceptor خواست logout کند
    useEffect(() => {
        const handler = () => logout();
        window.addEventListener('app:logout', handler);
        return () => window.removeEventListener('app:logout', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value: AuthContextType = {
        isAuthenticated,
        agent,
        token,
        loading,
        login,
        logout,
        refreshAgent,
        setAgent,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
